/**
 * Central runtime store for the Standard Restaurant Template.
 *
 * Pattern mirrors template-service/lib/_data.js — every export is a mutable
 * module-scope object/array so the template's static ES imports keep working
 * (e.g. `import { SITE, MENU_SECTIONS } from "../lib/_data"`), while the
 * shell calls `setRestaurantData(config)` synchronously before render to
 * swap in real business content.
 *
 * The template's Landing.jsx uses:
 *   - SITE.brand, SITE.eyebrow, SITE.hero_title (+ hero_title_em), hero_subtitle,
 *     hero_reserve_cta, hero_menu_cta, hero_image, hero_top_right_1, hero_top_right_2
 *   - STATS (4 short brand-strip facts)
 *   - MENU_INTRO { eyebrow, title, title_em, blurb, price_line }
 *   - MENU_SECTIONS [{ title, subtitle, items: [{ name, desc, price }] }]
 *   - RESERVATIONS { eyebrow, title, title_em, seatings, nights, duration, note }
 *   - ABOUT { eyebrow, title, title_em, paragraphs, image_1, image_2 }
 *   - CONTACT { eyebrow, title, title_em, address, hours, phone, email,
 *               private_events_email, copyright }
 */

export const SITE = {
    brand: "Your Restaurant",
    eyebrow: "Local · Seasonal · Handmade",
    hero_title: "A restaurant",
    hero_title_em: "worth returning to.",
    hero_subtitle: "Seasonal cooking made with care, served in a room that feels like home.",
    hero_reserve_cta: "Reserve a Table",
    hero_menu_cta: "View the Menu",
    hero_image: "",
    hero_top_right_1: "Est.",
    hero_top_right_2: "Chef's tasting menu",
};

export const STATS = [
    "Seasonal menu",
    "Weekly tasting",
    "Wine pairing available",
    "Intimate seating",
];

export const MENU_INTRO = {
    eyebrow: "The Menu",
    title: "A single tasting,",
    title_em: "composed this week.",
    blurb: "Our menu is guided by what's fresh and in season. Every week the chef begins again with what the market, garden, and coast offer that morning.",
    price_line: "",  // optional — e.g. "Tasting menu · 85 · Wine pairing +45"
};

export const MENU_SECTIONS = [];

export const RESERVATIONS = {
    eyebrow: "Reservations",
    title: "Come for the food.",
    title_em: "Stay for the evening.",
    seatings: [],           // [{ label, time }]
    nights: "",
    duration: "",
    note: "A request is not a confirmation. Our team will reach out to confirm your seating within one working day.",
};

// Iter-77 — visible form copy (labels/placeholders/submit/disclaimer).
// Populated exclusively via copy_overrides (`forms.reservation.*`).
export const FORMS = {};

export const ABOUT = {
    eyebrow: "The House",
    title: "A place",
    title_em: "with a story.",
    paragraphs: [],
    image_1: "",
    image_2: "",
};

export const CONTACT = {
    eyebrow: "Contact",
    title: "Find us",
    title_em: "in the neighbourhood.",
    address_lines: [],
    hours_lines: [],
    phone: "",
    email: "",
    private_events_email: "",
    copyright: "",
};

/**
 * Phase 3 site-chrome (header + footer). Mirrors template-service exports so
 * the inline visual editor can PATCH `header_config` / `footer_config` and
 * have both templates consume the same shape.
 */
export const HEADER = {
    logo_text: "",
    logo_image_url: "",
    show_phone: false,
    cta_text: "",
    cta_link: "",
    cta_open_new_tab: false,
    nav_items: [],
};

export const FOOTER = {
    tagline: "",
    copyright: "",
    socials: [],
    quick_links: [],
    newsletter_enabled: false,
    newsletter_heading: "",
    newsletter_subtitle: "",
    newsletter_cta_text: "Subscribe",
    show_contact: true,
    show_address: true,
};

export const BRANDING = { primary: "", accent: "", neutral: "" };

// Per-element Style / Advanced overrides (Phase 3B).
export const ELEMENT_STYLES = {};
export const ELEMENT_ADVANCED = {};

export function withElStyle(key, existing = {}) {
    const s = ELEMENT_STYLES[key];
    const adv = ELEMENT_ADVANCED[key];
    if (!s && !adv) return existing;

    const style = { ...(existing.style || {}) };
    const extraClasses = [];
    if (s) {
        if (s.background_color) style.backgroundColor = s.background_color;
        if (s.text_color) style.color = s.text_color;
        if (s.text_align) style.textAlign = s.text_align;
        const paddingClass = { sm: "!py-8", md: "!py-16", lg: "!py-24" }[s.padding];
        if (paddingClass) extraClasses.push(paddingClass);
    }
    if (adv && adv.css_class) extraClasses.push(adv.css_class);
    const className = extraClasses.length
        ? [existing.className, ...extraClasses].filter(Boolean).join(" ")
        : existing.className;
    const idProp = adv && adv.anchor_id ? { id: adv.anchor_id } : {};
    return { ...existing, ...idProp, className, style };
}

/** Mutate the exported objects in place with the backend's `restaurant_config`
 * payload. Called by the shell BEFORE mounting the template. */
export function setRestaurantData(config) {
    if (!config || typeof config !== "object") return;

    const merge = (target, src) => {
        if (!src || typeof src !== "object") return;
        for (const k of Object.keys(target)) delete target[k];
        Object.assign(target, src);
    };
    const replaceArr = (target, next) => {
        target.length = 0;
        if (Array.isArray(next)) for (const item of next) target.push(item);
    };

    if (config.site) merge(SITE, { ...SITE, ...config.site });
    if (Array.isArray(config.stats)) replaceArr(STATS, config.stats);

    if (config.menu_intro) merge(MENU_INTRO, { ...MENU_INTRO, ...config.menu_intro });
    if (Array.isArray(config.menu_sections)) replaceArr(MENU_SECTIONS, config.menu_sections);

    if (config.reservations) merge(RESERVATIONS, { ...RESERVATIONS, ...config.reservations });
    if (config.about) merge(ABOUT, { ...ABOUT, ...config.about });
    if (config.contact) merge(CONTACT, { ...CONTACT, ...config.contact });
    // Iter-77 — form copy overrides live under config.forms.*
    if (config.forms) merge(FORMS, { ...FORMS, ...config.forms });
    else for (const k of Object.keys(FORMS)) delete FORMS[k];

    // Phase 3 chrome. Same reset+assign pattern as template-service so a
    // fresh biz doesn't inherit the previous biz's header / footer state.
    const hydrateDict = (target, src) => {
        for (const k of Object.keys(target)) {
            if (Array.isArray(target[k])) target[k].length = 0;
            else if (typeof target[k] === "boolean") target[k] = false;
            else target[k] = "";
        }
        if (!src || typeof src !== "object") return;
        for (const k of Object.keys(src)) {
            const v = src[k];
            if (Array.isArray(v)) {
                if (!Array.isArray(target[k])) target[k] = [];
                target[k].length = 0;
                for (const item of v) target[k].push(item);
            } else {
                target[k] = v;
            }
        }
    };
    if (config.header) hydrateDict(HEADER, config.header);
    if (config.footer) hydrateDict(FOOTER, config.footer);
    if (config.branding && typeof config.branding === "object") {
        for (const k of Object.keys(BRANDING)) BRANDING[k] = "";
        Object.assign(BRANDING, config.branding);
    }
    if (config.element_styles && typeof config.element_styles === "object") {
        Object.keys(ELEMENT_STYLES).forEach((k) => delete ELEMENT_STYLES[k]);
        Object.assign(ELEMENT_STYLES, config.element_styles);
    }
    if (config.element_advanced && typeof config.element_advanced === "object") {
        Object.keys(ELEMENT_ADVANCED).forEach((k) => delete ELEMENT_ADVANCED[k]);
        Object.assign(ELEMENT_ADVANCED, config.element_advanced);
    }
}
